import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@porchpass/api-client";
import { toast } from "@porchpass/ui";
import { useAuth, useUser } from "src/context";
import { dealerStorage, getDealerNameFromUrl } from "src/utils";

const BORROWER_OWNERSHIP_MESSAGES: Record<string, string> = {
  borrower_not_owned:
    "You can't edit this section. It belongs to the other borrower on this application, so only they can change it.",
  borrower_invited_separately:
    "This borrower was invited to complete their own section, so it can't be changed here.",
};

/**
 * The message for a 409 the API returns when a borrower writes to the other
 * borrower's record (DEA-903), or undefined for any other error.
 */
export function getBorrowerOwnershipErrorMessage(
  error: unknown,
): string | undefined {
  if (!(error instanceof ApiError) || error.status !== 409) return undefined;
  const code = (error.body as { code?: unknown } | undefined)?.code;
  return typeof code === "string"
    ? BORROWER_OWNERSHIP_MESSAGES[code]
    : undefined;
}

export function useApiErrorHandler() {
  const { user } = useAuth();
  const { userData } = useUser();
  const navigate = useNavigate();

  const handleApiError = useCallback(
    (error: Error) => {
      const ownershipMessage = getBorrowerOwnershipErrorMessage(error);
      if (ownershipMessage) {
        toast.error(ownershipMessage);
        return;
      }

      if (!user || !userData || !userData.dealer_id) {
        return;
      }

      if (!(error instanceof ApiError)) {
        return;
      }

      const message = error.message?.toLowerCase() ?? "";

      if (
        error.status === 404 &&
        ((userData.application_id && message.includes("application")) ||
          (userData.borrower_id && message.includes("borrower")))
      ) {
        console.warn("Not found error, rerouting to dashboard");
        if (user.email) {
          dealerStorage.setItem("email-for-register", user.email);
        }
        if (user.phoneNumber) {
          dealerStorage.setItem("phone-for-register", user.phoneNumber);
        }
        navigate(`/${getDealerNameFromUrl()}/dashboard`);
        return;
      }

      if (error.status === 401 || message.includes("unauthorized")) {
        console.warn("Unauthorized error, rerouting to unauthorized page");
        dealerStorage.clear();
        navigate(`/${getDealerNameFromUrl()}/unauthorized`);
      }
    },
    [navigate, user, userData],
  );

  return { handleApiError };
}
